// 极值表数据准备层
// 职责：从分析项中计算最大值和最小值，输出表格所需的数据结构

import { AnalysisItem, LegendItem } from "helpers/models";
import {
  EXTREME_KEY_LAYER_RESULT,
  getFathom1000YearsDepth,
  isFathomLayer
} from "helpers/groupResultData";
import {
  ExtremeTableRowData,
  LayerExtremeState,
  LineAnalysisPoint,
  RuleIndexMap
} from "./types";
import { getLayerSortIndex, INVALID_VALUE_LABELS } from "./layer-meta";

/**
 * 标签值归一化（针对年期标签）
 * 例：Fathom 层的 "5 years" → "≤50 years"
 */
const normalizeYearLabel = (label: string, layerId: string): string => {
  if (layerId.includes("Fathom")) {
    if (["5 years", "10 years", "20 years", "50 years"].includes(label)) {
      return "≤50 years";
    }
    if (label === "1000 years") {
      return "≥1000 years";
    }
  }

  if (layerId === "FL_Fluvial_Official" && label === "> 500 years") {
    return "> 500 years (US)";
  }

  return label;
};

/**
 * 构建规则索引映射
 * 用于快速查询某图层的某标签在规则中的位置
 */
const buildRuleIndexMap = (legendItems: LegendItem[]): RuleIndexMap =>
  legendItems.reduce<RuleIndexMap>((acc, legendItem) => {
    const layerRuleMap = new Map<string, number>();
    legendItem.rules.forEach((rule, index) => {
      layerRuleMap.set(rule.title, index);
    });
    acc[legendItem.layerId] = layerRuleMap;
    return acc;
  }, {});

/**
 * 获取可比较的数值
 * 用于极值比较：
 * - Fathom 提取深度数值
 * - 非 Fathom 提取年期索引或数值
 */
const getComparableValue = (
  item: LineAnalysisPoint,
  ruleIndexMap: RuleIndexMap
): number | null => {
  if (item.layerId.includes("Fathom")) {
    const depth = getFathom1000YearsDepth(
      item,
      isFathomLayer(item.layerId) ? "Undefended" : "Defended"
    );
    const depthValue = parseFloat(depth);
    return Number.isNaN(depthValue) ? null : depthValue;
  }

  const extremeKey = EXTREME_KEY_LAYER_RESULT[item.layerId];
  if (!extremeKey) {
    return null;
  }

  const rawValue = item.details?.[extremeKey.key];
  if (rawValue === undefined || rawValue === null || rawValue === "") {
    return null;
  }

  if (extremeKey.type === "number") {
    const numberValue = parseFloat(String(rawValue));
    return Number.isNaN(numberValue) ? null : numberValue;
  }

  const label = normalizeYearLabel(String(rawValue), item.layerId);
  const sortIndex = ruleIndexMap[item.layerId]?.get(label);
  return sortIndex === undefined ? null : sortIndex;
};

/**
 * 获取极值标题
 * 用于表头显示当前图层的数值单位（例："最大洪水深度 [m]"）
 */
const getValueTitle = (t: any, layerId: string): string => {
  if (layerId.includes("Fathom")) {
    return t("legend.Maximum Flood Depth[m]");
  }

  const extremeKey = EXTREME_KEY_LAYER_RESULT[layerId];
  if (!extremeKey) {
    return t("drawShape.Extreme Values");
  }

  return t("legend." + extremeKey.key);
};

/**
 * 获取极值显示值
 * 用于表格中展示的格式化值（例："0.5" 或 "High"）
 */
const getDisplayValue = (
  t: any,
  layerId: string,
  item: LineAnalysisPoint
): string => {
  if (layerId.includes("Fathom")) {
    const depth = getFathom1000YearsDepth(
      item,
      isFathomLayer(layerId) ? "Undefended" : "Defended"
    );
    const depthValue = parseFloat(depth);
    if (Number.isNaN(depthValue)) {
      return t("legend." + depth);
    }
    return depth;
  }

  const extremeKey = EXTREME_KEY_LAYER_RESULT[layerId];
  if (!extremeKey) {
    return "-";
  }

  const rawValue = item.details?.[extremeKey.key];
  if (rawValue === undefined || rawValue === null || rawValue === "") {
    return "-";
  }

  if (extremeKey.type === "number") {
    return String(rawValue);
  }

  return t("legend." + String(rawValue));
};

/**
 * 获取坐标字符串
 */
const getCoordinate = (item: LineAnalysisPoint): string => {
  const longitude = item.longitude ?? "-";
  const latitude = item.latitude ?? "-";
  return `${longitude}, ${latitude}`;
};

const getDistanceFromStartMeters = (
  layerId: string,
  type: "max" | "min",
  item: LineAnalysisPoint,
  extremeMap?: Map<string, any>
): number | null => {
  const directDistance = Number((item as any)?.distanceFromStartMeters);
  if (Number.isFinite(directDistance)) {
    return directDistance;
  }

  const layerExtreme = extremeMap?.get(layerId);
  if (!layerExtreme) {
    return null;
  }

  const pointDistance = Number(layerExtreme?.[type]?.distanceFromStartMeters);
  if (Number.isFinite(pointDistance)) {
    return pointDistance;
  }

  const fallbackDistance = Number(
    layerExtreme?.[
      type === "max"
        ? "maxDistanceFromStartMeters"
        : "minDistanceFromStartMeters"
    ]
  );
  return Number.isFinite(fallbackDistance) ? fallbackDistance : null;
};

const formatDistance = (
  distanceInMeters: number | null,
  distanceUnit: "m" | "km"
) => {
  if (distanceInMeters === null || !Number.isFinite(distanceInMeters)) {
    return "-";
  }

  if (distanceUnit === "km") {
    return (distanceInMeters / 1000).toFixed(3);
  }

  return distanceInMeters.toFixed(3);
};

/**
 * 构建极值表数据
 * 输入：分析项、图例定义、i18n 函数
 * 输出：每个图层的最大值和最小值行数据
 */
export const buildExtremeTableData = (
  t: any,
  analysisItems: AnalysisItem[],
  legendItems: LegendItem[],
  distanceUnit: "m" | "km",
  extremeMap?: Map<string, any>
): ExtremeTableRowData[] => {
  const ruleIndexMap = buildRuleIndexMap(legendItems);
  const extremeByLayer: Record<string, LayerExtremeState> = {};

  // 遍历所有分析项，计算每图层的极值
  analysisItems.forEach((rawItem) => {
    const item = rawItem as LineAnalysisPoint;

    // 跳过无效标签
    if (INVALID_VALUE_LABELS.has(item.valueLabel)) {
      return;
    }

    // 计算可比较值
    const comparableValue = getComparableValue(item, ruleIndexMap);
    if (comparableValue === null) {
      return;
    }

    // 初始化或更新该图层的极值
    const layerState = extremeByLayer[item.layerId];
    if (!layerState) {
      extremeByLayer[item.layerId] = {
        max: { ...item },
        min: { ...item },
        maxValue: comparableValue,
        minValue: comparableValue,
        maxSameValueItems: [{ ...item }],
        minSameValueItems: [{ ...item }]
      };
      return;
    }

    // 更新最大值
    if (comparableValue > layerState.maxValue) {
      layerState.max = { ...item };
      layerState.maxValue = comparableValue;
      layerState.maxSameValueItems = [{ ...item }];
    } else if (comparableValue === layerState.maxValue) {
      layerState.maxSameValueItems.push({ ...item });
    }

    // 更新最小值
    if (comparableValue < layerState.minValue) {
      layerState.min = { ...item };
      layerState.minValue = comparableValue;
      layerState.minSameValueItems = [{ ...item }];
    } else if (comparableValue === layerState.minValue) {
      layerState.minSameValueItems.push({ ...item });
    }
  });

  // 按图层顺序排序
  const sortLayerIds = Object.keys(extremeByLayer).sort((a, b) => {
    const indexA = getLayerSortIndex(a);
    const indexB = getLayerSortIndex(b);

    if (
      indexA === Number.MAX_SAFE_INTEGER &&
      indexB === Number.MAX_SAFE_INTEGER
    ) {
      return a.localeCompare(b);
    }
    if (indexA === Number.MAX_SAFE_INTEGER) return 1;
    if (indexB === Number.MAX_SAFE_INTEGER) return -1;
    return indexA - indexB;
  });

  // 输出表格行数据
  const distanceTitle = `${t("drawShape.distance")}(${distanceUnit === "km" ? t("drawShape.km") : t("drawShape.m")})`;

  return sortLayerIds.map((layerId) => {
    const layerState = extremeByLayer[layerId];
    const maxSameValueCount = layerState.maxSameValueItems.length;
    const minSameValueCount = layerState.minSameValueItems.length;

    return {
      layerId,
      valueTitle: getValueTitle(t, layerId),
      distanceTitle,
      maxCoordinate: getCoordinate(layerState.max),
      minCoordinate: getCoordinate(layerState.min),
      maxDistance: formatDistance(
        getDistanceFromStartMeters(layerId, "max", layerState.max, extremeMap),
        distanceUnit
      ),
      minDistance: formatDistance(
        getDistanceFromStartMeters(layerId, "min", layerState.min, extremeMap),
        distanceUnit
      ),
      maxValue: getDisplayValue(t, layerId, layerState.max),
      minValue: getDisplayValue(t, layerId, layerState.min),
      maxHasMultipleLocations: maxSameValueCount > 1,
      minHasMultipleLocations: minSameValueCount > 1
    };
  });
};
